import { ConsentBanner, ConsentConfig, ConsentMode } from "../types";

/**
 * The brand's tracking consent configuration, read from what the backend
 * injects into the portal HTML before React boots:
 *
 * ```html
 * <meta name="dapple-consent" content='{"v":1,"mode":"OptIn",...}'>
 * ```
 *
 * inserted right after `<head>`, every key present:
 * `v, mode, banner, policyVersion, title, message, acceptLabel, rejectLabel,
 * chooseLabel, privacyUrl, primaryColour, primaryTextColour, cookieDomain`.
 *
 * THE ONE PLACE that knows where that injection lives. Everything else (the
 * consent store, the Datadog boot path, the banner) asks this module.
 *
 * Read synchronously: `initTelemetry` runs at module scope from `index.web.js`
 * and has to decide `trackingConsent` before the SDK sends anything. The meta
 * tag is in the served HTML, so it is in the DOM before any script runs.
 *
 * A missing, partial or malformed tag reads as NotRequired, which is today's
 * behaviour. That covers native (no document), the clinic app and whitelabel
 * domains (no tag), local dev (Expo serves the HTML) and any brand the backend
 * has not configured. Only the backend can put a brand into OptIn, so failing
 * open here never overrides a clinic's real choice.
 */
export const CONSENT_META_NAME = "dapple-consent";

/** What a brand with no consent configuration behaves as. */
export const DEFAULT_CONSENT_CONFIG: ConsentConfig = {
  mode: "NotRequired",
  banner: "DappleOS",
  policyVersion: "",
};

/** Upper bound on any injected copy string, so a bad config cannot bloat the banner. */
const MAX_COPY_LENGTH = 2000;

const asMode = (value: unknown): ConsentMode =>
  value === "OptIn" ? "OptIn" : "NotRequired";

const asBanner = (value: unknown): ConsentBanner =>
  value === "OwnTool" ? "OwnTool" : "DappleOS";

/** A trimmed, bounded string, or undefined for anything empty or not a string. */
const asCopy = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > MAX_COPY_LENGTH) return undefined;
  return trimmed;
};

/**
 * Only https URLs (the server allows nothing else): the privacy link is
 * rendered as a link, and a `javascript:` URL must never become one.
 */
const asHttpsUrl = (value: unknown): string | undefined => {
  const copy = asCopy(value);
  if (!copy) return undefined;
  return /^https:\/\//i.test(copy) ? copy : undefined;
};

/** `#rgb` or `#rrggbb` only, since the value lands in a style. */
const asHexColour = (value: unknown): string | undefined => {
  const copy = asCopy(value);
  if (!copy) return undefined;
  return /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(copy) ? copy : undefined;
};

/** A bare hostname (`clinic.com.au`), as a cookie `domain` attribute takes. */
const asCookieDomain = (value: unknown): string | undefined => {
  const copy = asCopy(value)?.toLowerCase().replace(/^\./, "");
  if (!copy) return undefined;
  return /^[a-z0-9-]+(?:\.[a-z0-9-]+)+$/.test(copy) ? copy : undefined;
};

/** Upper bound on injected hand-off hosts, so a bad config cannot bloat checks. */
const MAX_HANDOFF_HOSTS = 200;

/** Plain lower-case hostnames only; anything else is dropped. */
const asHostList = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  const hosts: string[] = [];
  for (const item of value.slice(0, MAX_HANDOFF_HOSTS)) {
    if (typeof item !== "string") continue;
    const host = item.trim().toLowerCase();
    if (/^[a-z0-9-]+(?:\.[a-z0-9-]+)+$/.test(host)) hosts.push(host);
  }
  return hosts;
};

/**
 * Normalises an untrusted object into a `ConsentConfig`. Exported for tests and
 * for any future source that needs the same rules.
 */
export const normaliseConsentConfig = (raw: unknown): ConsentConfig => {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return DEFAULT_CONSENT_CONFIG;
  }

  const input = raw as Record<string, unknown>;

  return {
    mode: asMode(input.mode),
    banner: asBanner(input.banner),
    title: asCopy(input.title),
    message: asCopy(input.message),
    acceptLabel: asCopy(input.acceptLabel),
    rejectLabel: asCopy(input.rejectLabel),
    chooseLabel: asCopy(input.chooseLabel),
    privacyUrl: asHttpsUrl(input.privacyUrl),
    primaryColour: asHexColour(input.primaryColour),
    primaryTextColour: asHexColour(input.primaryTextColour),
    cookieDomain: asCookieDomain(input.cookieDomain),
    tenant: asCopy(input.tenant),
    handoffHosts: asHostList(input.handoffHosts),
    policyVersion: asCopy(input.policyVersion) ?? "",
  };
};

/**
 * The injected configuration for this page, or NotRequired when there is none.
 * Never throws.
 */
export const readConsentConfig = (): ConsentConfig => {
  try {
    const doc = globalThis.document;
    if (typeof doc?.querySelector !== "function") return DEFAULT_CONSENT_CONFIG;

    const meta = doc.querySelector<HTMLMetaElement>(
      `meta[name="${CONSENT_META_NAME}"]`,
    );
    const content = meta?.getAttribute("content");
    if (!content) return DEFAULT_CONSENT_CONFIG;

    return normaliseConsentConfig(JSON.parse(content));
  } catch {
    return DEFAULT_CONSENT_CONFIG;
  }
};
